import React, { useEffect, useState } from "react";
import {
  fetchAdminSubmissionDetail,
  approveSubmission,
  rejectSubmission,
  STATUS_META,
  TYPE_LABEL,
} from "@/api/adminSubmissions";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
  Globe,
  MapPin,
  Calendar,
  User as UserIcon,
  Tag,
  Instagram,
  Facebook,
  ArrowRight,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_LABELS = {
  "eten-drinken": "Eten & Drinken",
  winkelen: "Winkelen",
  diensten: "Diensten",
  beleven: "Zien & Doen",
  verblijf: "Overnachten",
};
const FIELD_LABELS = {
  description_nl: "Omschrijving",
  website: "Website",
  phone: "Telefoon",
  image: "Foto",
  opening_hours: "Openingstijden",
  email: "E-mail",
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || {
    label: status,
    color: "#5A7594",
    textColor: "#FFFFFF",
  };
  return (
    <span
      className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-[Poppins] font-semibold uppercase tracking-wider"
      style={{ backgroundColor: meta.color, color: meta.textColor }}
    >
      {meta.label}
    </span>
  );
};

const InfoRow = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-2 text-sm text-[#3E5871] font-[Inter]">
    <Icon className="w-4 h-4 text-[#2E9CD6] mt-0.5 shrink-0" />
    <span className="min-w-0 break-words">{children}</span>
  </div>
);

const DiffTable = ({ diff, linkedListing }) => {
  if (!diff) return null;
  return (
    <div
      data-testid="diff-table"
      className="mt-4 rounded-xl border border-[#E4EEF5] overflow-hidden"
    >
      <div className="bg-[#F4F8FB] px-4 py-2 border-b border-[#E4EEF5] flex items-center justify-between">
        <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594]">
          Wijzigingen op bestaande listing
        </p>
        {linkedListing ? (
          <Link
            to={`/bedrijf/${linkedListing.slug}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-[#2E9CD6] font-[Poppins] font-semibold hover:text-[#173A5E]"
          >
            {linkedListing.name}
            <ExternalLink className="w-3 h-3" />
          </Link>
        ) : (
          <span className="text-xs text-[#CE1B2A] font-[Poppins] font-semibold">
            Geen bijbehorende listing gevonden
          </span>
        )}
      </div>
      {diff.length === 0 ? (
        <p className="p-4 text-sm text-[#5A7594] font-[Inter]">
          Geen ingevulde velden om over te nemen.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-[#F9FBFD]">
            <tr>
              <th className="text-left px-4 py-2 font-[Poppins] font-semibold text-[#5A7594] text-xs uppercase tracking-wider">
                Veld
              </th>
              <th className="text-left px-4 py-2 font-[Poppins] font-semibold text-[#5A7594] text-xs uppercase tracking-wider">
                Huidig
              </th>
              <th className="text-left px-4 py-2 font-[Poppins] font-semibold text-[#5A7594] text-xs uppercase tracking-wider">
                Nieuw
              </th>
            </tr>
          </thead>
          <tbody>
            {diff.map((row) => (
              <tr
                key={row.field}
                className={`border-t border-[#E4EEF5] ${
                  row.would_change ? "bg-[#F3C300]/10" : ""
                }`}
              >
                <td className="px-4 py-2 font-[Poppins] font-semibold text-[#173A5E] align-top">
                  {FIELD_LABELS[row.field] || row.field}
                  {row.would_change && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-[#CE1B2A] font-bold">
                      wijzigt
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-[#5A7594] align-top max-w-[240px]">
                  <span className="line-clamp-3 break-words">
                    {row.current || <em className="text-[#8AA3B8]">— leeg —</em>}
                  </span>
                </td>
                <td className="px-4 py-2 text-[#173A5E] align-top max-w-[280px]">
                  <span className="line-clamp-3 break-words">
                    {row.new}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const RejectPanel = ({ onCancel, onConfirm, submitting }) => {
  const [reason, setReason] = useState("");
  return (
    <div
      data-testid="reject-panel"
      className="mt-4 p-4 rounded-xl bg-[#FDECEE] border border-[#F5C3C8]"
    >
      <label className="block text-sm font-[Poppins] font-semibold text-[#8b1420] mb-2">
        Reden voor afwijzing (optioneel)
      </label>
      <textarea
        data-testid="reject-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Bijv. incomplete gegevens, past niet bij Lemmer Leeft…"
        className="w-full bg-white border border-[#F5C3C8] rounded-lg px-3 py-2 text-sm font-[Inter] text-[#173A5E] outline-none focus:border-[#CE1B2A] resize-y min-h-[70px]"
      />
      <div className="mt-3 flex flex-wrap gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 rounded-full bg-white text-[#173A5E] border border-[#E4EEF5] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm transition"
        >
          Annuleer
        </button>
        <button
          type="button"
          onClick={() => onConfirm(reason)}
          disabled={submitting}
          data-testid="reject-confirm"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#CE1B2A] hover:bg-[#a51420] text-white font-[Poppins] font-semibold text-sm transition disabled:opacity-60"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <XCircle className="w-4 h-4" />
          )}
          Bevestig afwijzing
        </button>
      </div>
    </div>
  );
};

export const SubmissionCard = ({ submission: initial, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null); // 'approve'|'reject'|null
  const [showReject, setShowReject] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const sub = detail?.submission || initial;

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail && !loading) {
      setLoading(true);
      try {
        const d = await fetchAdminSubmissionDetail(initial.id);
        setDetail(d);
      } catch {
        setError("Kon details niet laden.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleApprove = async () => {
    setError("");
    setAction("approve");
    try {
      const res = await approveSubmission(initial.id);
      setResult({ kind: "approved", ...res });
      if (onChanged) onChanged();
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Goedkeuren mislukt. Probeer het opnieuw.",
      );
      setAction(null);
    }
  };

  const handleReject = async (reason) => {
    setError("");
    setAction("reject");
    try {
      await rejectSubmission(initial.id, reason);
      setResult({ kind: "rejected", reason });
      setShowReject(false);
      if (onChanged) onChanged();
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Afwijzen mislukt. Probeer het opnieuw.",
      );
      setAction(null);
    }
  };

  const cat = sub?.categorie;
  const catLabel = CATEGORY_LABELS[cat] || cat || "—";
  const fotoAbs = sub?.foto_url
    ? sub.foto_url.startsWith("http")
      ? sub.foto_url
      : `${BACKEND_URL}${sub.foto_url}`
    : null;

  const isPending = sub?.status === "pending" || sub?.status === "nieuw" || !sub?.status;

  return (
    <article
      data-testid={`submission-${initial.id}`}
      className="bg-white rounded-2xl border border-[#E4EEF5] shadow-sm overflow-hidden"
    >
      {/* Top bar */}
      <div className="p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <StatusBadge status={sub?.status || "pending"} />
              <span
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-[Poppins] font-semibold"
                style={{
                  backgroundColor:
                    sub?.type === "aanvulling" ? "#6BC0E8" : "#2E9CD6",
                  color: "#FFFFFF",
                }}
              >
                {TYPE_LABEL[sub?.type] || sub?.type}
              </span>
              {cat && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold bg-[#F4F8FB] text-[#173A5E] border border-[#E4EEF5]">
                  <Tag className="w-3 h-3" />
                  {catLabel}
                </span>
              )}
            </div>
            <h3
              data-testid="submission-name"
              className="text-xl sm:text-2xl font-[Poppins] font-bold text-[#173A5E]"
            >
              {sub?.bedrijfsnaam}
            </h3>
            <p className="text-xs text-[#5A7594] mt-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Ingediend {formatDate(sub?.created_at)}
            </p>
          </div>
          <button
            type="button"
            onClick={toggleOpen}
            data-testid="submission-toggle"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-[Poppins] font-semibold text-[#2E9CD6] hover:bg-[#F4F8FB] transition"
          >
            {open ? "Verberg details" : "Bekijk details"}
            {open ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Compact info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-3">
          <InfoRow icon={UserIcon}>{sub?.contactpersoon || "—"}</InfoRow>
          <InfoRow icon={Mail}>
            {sub?.email ? (
              <a href={`mailto:${sub.email}`} className="text-[#2E9CD6]">
                {sub.email}
              </a>
            ) : (
              "—"
            )}
          </InfoRow>
          {sub?.telefoon && <InfoRow icon={Phone}>{sub.telefoon}</InfoRow>}
          {sub?.website && (
            <InfoRow icon={Globe}>
              <a
                href={sub.website}
                target="_blank"
                rel="noreferrer"
                className="text-[#2E9CD6] break-all"
              >
                {sub.website}
              </a>
            </InfoRow>
          )}
        </div>

        {/* Expanded */}
        {open && (
          <div className="mt-5 pt-5 border-t border-[#E4EEF5]">
            {loading ? (
              <div className="flex items-center gap-2 text-[#5A7594]">
                <Loader2 className="w-4 h-4 animate-spin" />
                Details laden…
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="md:col-span-2 space-y-3">
                    {sub?.omschrijving_nl && (
                      <div>
                        <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594] mb-1">
                          Omschrijving
                        </p>
                        <p className="text-sm text-[#3E5871] font-[Inter] whitespace-pre-line">
                          {sub.omschrijving_nl}
                        </p>
                      </div>
                    )}
                    {sub?.openingstijden && (
                      <div>
                        <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594] mb-1">
                          Openingstijden
                        </p>
                        <p className="text-sm text-[#3E5871] font-[Inter] whitespace-pre-line">
                          {sub.openingstijden}
                        </p>
                      </div>
                    )}
                    {(sub?.adres || sub?.postcode) && (
                      <InfoRow icon={MapPin}>
                        {[sub.adres, sub.postcode].filter(Boolean).join(", ")}
                      </InfoRow>
                    )}
                    {sub?.instagram && (
                      <InfoRow icon={Instagram}>{sub.instagram}</InfoRow>
                    )}
                    {sub?.facebook && (
                      <InfoRow icon={Facebook}>{sub.facebook}</InfoRow>
                    )}
                    {sub?.membership && (
                      <p className="text-xs text-[#5A7594] font-[Inter]">
                        Lidmaatschap:{" "}
                        <span className="font-semibold text-[#173A5E]">
                          {sub.membership}
                        </span>
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594] mb-2">
                      Foto
                    </p>
                    {fotoAbs ? (
                      <a
                        href={fotoAbs}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="submission-photo-link"
                      >
                        <img
                          src={fotoAbs}
                          alt="Ingezonden foto"
                          className="w-full h-40 object-cover rounded-xl border border-[#E4EEF5]"
                        />
                      </a>
                    ) : (
                      <div className="w-full h-40 rounded-xl border border-dashed border-[#E4EEF5] bg-[#F4F8FB] flex items-center justify-center text-[#8AA3B8] text-sm font-[Inter]">
                        Geen foto
                      </div>
                    )}
                  </div>
                </div>

                {sub?.type === "aanvulling" && detail && (
                  <DiffTable
                    diff={detail.diff}
                    linkedListing={detail.linked_listing}
                  />
                )}

                {sub?.reject_reason && (
                  <div className="mt-4 p-3 rounded-xl bg-[#FDECEE] border border-[#F5C3C8]">
                    <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#8b1420] mb-1">
                      Reden afwijzing
                    </p>
                    <p className="text-sm text-[#8b1420] font-[Inter]">
                      {sub.reject_reason}
                    </p>
                  </div>
                )}
                {sub?.reviewed_at && (
                  <p className="mt-3 text-xs text-[#5A7594] font-[Inter]">
                    {sub.status === "approved" ? "Goedgekeurd" : "Beoordeeld"} op{" "}
                    {formatDate(sub.reviewed_at)}
                    {sub.reviewed_by ? ` door ${sub.reviewed_by}` : ""}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      {isPending && !result && (
        <div className="border-t border-[#E4EEF5] bg-[#F9FBFD] p-4 sm:p-5">
          {error && (
            <div className="mb-3 p-3 rounded-lg bg-[#FDECEE] border border-[#F5C3C8] text-[#8b1420] text-sm">
              {error}
            </div>
          )}
          {showReject ? (
            <RejectPanel
              onCancel={() => setShowReject(false)}
              onConfirm={handleReject}
              submitting={action === "reject"}
            />
          ) : (
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowReject(true)}
                data-testid={`reject-btn-${initial.id}`}
                disabled={!!action}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-[#CE1B2A] border border-[#F5C3C8] hover:bg-[#FDECEE] font-[Poppins] font-semibold text-sm transition"
              >
                <XCircle className="w-4 h-4" />
                Afwijzen
              </button>
              <button
                type="button"
                onClick={handleApprove}
                data-testid={`approve-btn-${initial.id}`}
                disabled={!!action}
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#22a56b] hover:bg-[#1a8757] text-white font-[Poppins] font-semibold text-sm transition disabled:opacity-60"
              >
                {action === "approve" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                Goedkeuren
              </button>
            </div>
          )}
        </div>
      )}
      {result?.kind === "approved" && (
        <div
          data-testid={`approved-banner-${initial.id}`}
          className="border-t border-[#E4EEF5] bg-[#E7F7EF] p-4 flex items-center justify-between gap-3"
        >
          <p className="text-sm text-[#1a8757] font-[Poppins] font-semibold">
            Goedgekeurd — {result.action === "created" ? "nieuwe listing aangemaakt" : "listing bijgewerkt"}
            {result.slug ? ` (${result.slug})` : ""}.
          </p>
          {result.slug && (
            <Link
              to={`/bedrijf/${result.slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-[#22a56b] font-[Poppins] font-semibold"
            >
              Bekijk op de site
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      )}
      {result?.kind === "rejected" && (
        <div className="border-t border-[#E4EEF5] bg-[#FDECEE] p-4">
          <p className="text-sm text-[#8b1420] font-[Poppins] font-semibold">
            Inzending afgewezen.
          </p>
        </div>
      )}
    </article>
  );
};

export default SubmissionCard;
